import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";

import App from "./App";
import { AuthProvider } from "./auth/AuthProvider";
import {
  VERSIONED_GC_MS,
  installVersionInvalidation,
  versionedStaleTime,
  versionedStructuralSharing,
} from "./lib/dataVersion";
// Letters zelf gehost: geen verzoek naar een letterdienst van derden, en elke
// gebruiker ziet dezelfde letter. Stijl A (28-10-2026): Figtree voor tekst en
// koppen, Barlow Semi Condensed voor cijfers en kengetallen. Beide gratis (OFL).
// Alleen Latijns schrift en de gewichten die de tokens gebruiken.
import "@fontsource/figtree/latin-400.css";
import "@fontsource/figtree/latin-500.css";
import "@fontsource/figtree/latin-600.css";
import "@fontsource/figtree/latin-700.css";
import "@fontsource/barlow-semi-condensed/latin-400.css";
import "@fontsource/barlow-semi-condensed/latin-500.css";
import "@fontsource/barlow-semi-condensed/latin-600.css";
import "@fontsource/barlow-semi-condensed/latin-700.css";
import "./styles/tokens.css";
import "./styles/base.css";
import { viewportFor } from "./styles/breakpoints";
import { defaultDensity } from "./styles/density";

// Vóór de eerste weergave al de maat en een voorlopige dichtheid op <html>, zodat
// de schil niet eerst breed tekent en daarna verspringt. De schil neemt het
// daarna over (`useShellAttributes`), met de eigen keuze van de gebruiker.
document.documentElement.dataset.viewport = viewportFor(window.innerWidth);
document.documentElement.dataset.density = defaultDensity(window.innerWidth);

/**
 * Fail-closed ook in de cache: liever een verzoek te veel dan een cijfer dat
 * niet meer klopt. Een beleidswaarde die na een nieuwe parameterset verandert,
 * mag niet uit een oude cache blijven komen.
 *
 * Sinds plan 17 (E4) op versie in plaats van op tijd: een antwoord met een
 * dataversie is pas verouderd als de API een nieuwere versie meldt, en wordt
 * dan meteen ververst (`installVersionInvalidation`; opnieuw vragen is dan een
 * 304). Antwoorden zonder versie (account, feedback, bijwerkstatus) houden de
 * 30 s van voorheen. Wat er op het scherm staat en of dat actueel is, zegt de
 * markering in de kop (`DataVersionStatus`).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: versionedStaleTime(30_000),
      gcTime: VERSIONED_GC_MS,
      structuralSharing: versionedStructuralSharing,
      refetchOnWindowFocus: false,
      // Een 409 is geen storing maar een antwoord: niet opnieuw proberen.
      // Een 504 ook niet: dat is een vraag die de querygrens van de API
      // (`statement_timeout`, 30 s) heeft geraakt. Opnieuw proberen geeft
      // hetzelfde werk en dezelfde grens, dus de gebruiker wachtte ca. 99 s op
      // drie keer dezelfde fout in plaats van ca. 33 s op één (plan 16, R6).
      // Een 503 (database even niet bereikbaar, bijvoorbeeld na een uitrol)
      // blijft wél herhaald: die kan bij een tweede poging wel lukken.
      retry: (failureCount, error) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        if (status === 504) return false;
        return failureCount < 2;
      },
    },
  },
});

installVersionInvalidation(queryClient);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* De authenticatiecontext staat bewust bóven de router: de bewaking én het
        inlogscherm lezen dezelfde staat, en de provider mag niet opnieuw
        opstarten bij elke navigatie. */}
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </AuthProvider>
  </React.StrictMode>,
);
